/**
 * What KIND of appliance a catalogue entry describes — winder or safe.
 *
 * The admin mirror of `catalogApplianceKind` in watchlock's `utils/winderSpecs.ts`
 * (#1026). Kept as its own tiny module rather than folded into
 * `winderCatalogueQuery.ts` because three surfaces need the same answer — the
 * Curated Winders & Safes list segment, the editor's form seed, and the bulk
 * enrichment patch invariants — and a rule enforced in three places is a rule
 * that holds in two.
 *
 * ONE ARGUMENT, unlike the RN side. watchlock resolves the kind from a pair:
 * the user's own `UserWinder` instance first, its catalogue entry second, so an
 * owner can correct a miscatalogued entry without an admin. The admin app never
 * touches the owner-scoped side — it writes the catalogue and nothing else — so
 * the instance half has no meaning here and is deliberately absent rather than
 * accepted and ignored.
 *
 * Pure: a `WinderEntry` in, a `StorageApplianceKind` out. No Firestore, no state.
 */

import type { StorageApplianceKind, WinderEntry } from '../types';

/**
 * The five fields that make an appliance a winder rather than a safe.
 *
 * Exactly the winder-only set shared-types enumerates on `StorageApplianceKind`
 * — `powerSourceSlugs` is deliberately NOT among them, because a safe with an
 * electronic keypad has a power source too. Exported so the editor hides the
 * same five fields the classifier reads, and so `enforceWinderInvariants` clears
 * the same five on a patch. One list, three consumers.
 */
export const WINDER_ONLY_KEYS = [
  'turnsPerDay',
  'turnsPerDayRange',
  'rotationDirection',
  'rotationProgramSlugs',
  'startDelayMaxHours',
] as const;

/** The subset of a `WinderEntry` the classification actually reads. */
export type ApplianceKindInput = Pick<
  WinderEntry,
  'applianceKind' | 'isSafe' | (typeof WINDER_ONLY_KEYS)[number]
>;

/**
 * Whether an entry describes something that actually WINDS.
 *
 * An empty array is "we know of none", not evidence of a rotor — a safe whose
 * `turnsPerDay` was written `[]` rather than left absent (which `saveWinder`
 * never does, but a bulk patch or a hand-edit might) must still classify as a
 * safe. Hence the length checks rather than mere presence.
 *
 * Exported because the listing's Kind BADGES need it directly. They cannot use
 * {@link appearsInApplianceListing} for the winder badge: that predicate is
 * deliberately over-inclusive (an entry with no declared kind lists under
 * Winders, so a curator never loses a row), and an extra row in a segment is
 * ignorable in a way an "also winds" badge on a rotorless safe is not. A
 * segment answers "might this belong here"; a badge asserts.
 */
export function entryWinds(entry: ApplianceKindInput): boolean {
  return Boolean(
    entry.turnsPerDay?.length ||
      entry.turnsPerDayRange ||
      entry.rotationDirection ||
      entry.rotationProgramSlugs?.length ||
      entry.startDelayMaxHours,
  );
}

/**
 * Winder or safe, including for the entries that predate `applianceKind`.
 *
 * `applianceKind` is the answer whenever it is set. It will not always be set,
 * and this is the part that matters: safes were catalogued for months with
 * `isSafe: true` and nothing else to mark them, and an entry nobody edits again
 * never acquires the new field. Reading `applianceKind` directly is therefore
 * not a filter — it is a filter that finds nothing, which is exactly the bug
 * watchlock #1026 fixed on the RN picker and the same one the admin's own list
 * segment would otherwise ship with.
 *
 * ⚠️ THE FALLBACK BELOW IS PERMANENT, NOT A MIGRATION WINDOW. Do not delete it
 * once the catalogue is "backfilled": shared-types is explicit that absent means
 * winder and that no backfill should be run, so entries with no `applianceKind`
 * are the steady state rather than a queue that drains.
 *
 * ⚠️ `isSafe` ALONE IS NOT ENOUGH, and this is the subtle half. Its original and
 * still-current meaning is "this WINDER is built into a safe" — the Buben &
 * Zörweg Object case, a rotor plus security, which stays a winder. Filing every
 * `isSafe` entry under Safes would move exactly those top-of-market models out
 * of the Winders segment, and hide their turns-per-day behind the editor's
 * safe branch.
 *
 * So the tie-break is whether it WINDS. Something that protects and does not
 * wind is a safe; something that does both is a winder that is also a safe.
 * That is the same distinction `applianceKind` makes explicit, read off the
 * fields the catalogue already carries.
 *
 * Conservative in the ambiguous direction: anything with a winding field stays a
 * winder, so a mislabelled entry keeps the behaviour it has today rather than
 * silently changing category. An admin corrects it explicitly in the editor.
 */
export function resolveApplianceKind(
  entry: ApplianceKindInput | null | undefined,
): StorageApplianceKind {
  if (!entry) return 'winder';
  if (entry.applianceKind) return entry.applianceKind;
  if (entry.isSafe !== true) return 'winder';
  return entryWinds(entry) ? 'winder' : 'safe';
}

/**
 * Whether an entry should be LISTED under `kind`.
 *
 * ⚠️ NOT `resolveApplianceKind(entry) === kind`, and the difference is the whole
 * point: an entry belongs under more than one heading. A Barrington 12 Watch
 * Winder Safe winds AND protects; a Buben & Zörweg Collector does both and
 * displays. Forcing each into one bucket meant a curator filtering to Safes
 * could not find the safe they had catalogued, because it had been classified a
 * winder on the strength of having a rotor.
 *
 * ```
 * winder        applianceKind absent or 'winder'
 * safe          applianceKind === 'safe'         || isSafe === true
 * display case  applianceKind === 'display_case' || hasDisplayCase === true
 * ```
 *
 * The admin mirror of watchlock's `appearsInAppliancePicker` (#1031), and it
 * MUST stay identical: the two segment the same documents, and a disagreement
 * would have a curator and an owner looking at different catalogues.
 *
 * `isSafe` and `hasDisplayCase` carrying the second and third lines is what
 * makes this work with no backfill — most of the catalogue predates
 * `applianceKind` entirely.
 *
 * This supersedes the does-it-wind tie-break in {@link resolveApplianceKind} for
 * LISTING purposes. That function still answers "what is this primarily", which
 * is what the Kind column and the editor's seed need.
 */
export function appearsInApplianceListing(
  entry: Pick<WinderEntry, 'applianceKind' | 'isSafe' | 'hasDisplayCase'>,
  kind: StorageApplianceKind,
): boolean {
  if (kind === 'safe') {
    return entry.applianceKind === 'safe' || entry.isSafe === true;
  }
  if (kind === 'display_case') {
    return (
      entry.applianceKind === 'display_case' || entry.hasDisplayCase === true
    );
  }
  // An ALLOWLIST, not `!== 'safe'` — with three kinds those stop being the same
  // thing, and the old form would have listed a display case under Winders.
  return entry.applianceKind == null || entry.applianceKind === 'winder';
}

/** Convenience predicate — reads better than comparing at the call site. */
export function isSafeAppliance(
  entry: ApplianceKindInput | null | undefined,
): boolean {
  return resolveApplianceKind(entry) === 'safe';
}

/** Display label for a resolved kind. */
export const APPLIANCE_KIND_LABELS: Record<StorageApplianceKind, string> = {
  winder: 'Winder',
  safe: 'Safe',
  display_case: 'Display Case',
};

/**
 * Every kind the union admits, as a runtime list.
 *
 * ⚠️ DERIVED FROM {@link APPLIANCE_KIND_LABELS}, NOT WRITTEN OUT AGAIN, and the
 * indirection is the point rather than cleverness. That map is a
 * `Record<StorageApplianceKind, string>`, so the compiler refuses it the moment
 * shared-types grows a fourth kind and the label is missing — which makes this
 * array complete by construction instead of complete by whoever remembered.
 *
 * A hand-written copy is exactly what shipped the bug this export exists to
 * prevent. `winderCatalogueQuery` validated incoming `applianceKind` values
 * against a literal `['winder', 'safe']` that predated display cases and never
 * grew, so every display case came out of Algolia with its kind SILENTLY
 * DROPPED — no error, no unknown-value warning, just an entry that resolved to
 * "winder" because absent means winder. The Winders segment then held the whole
 * catalogue and every display case rendered "Winder + display case".
 *
 * The general trap: a union in shared-types, hand-copied into a runtime check,
 * drifting in silence because nothing compares the two. Import this rather than
 * typing the kinds out.
 */
export const STORAGE_APPLIANCE_KINDS = Object.keys(
  APPLIANCE_KIND_LABELS,
) as StorageApplianceKind[];

/**
 * What the editor's three capability checkboxes hold.
 *
 * ⚠️ CHECKBOXES, NOT A SEGMENTED CONTROL, and the change is more than cosmetic.
 * An appliance is frequently more than one of these at once — a Buben & Zörweg
 * Collector winds, protects AND displays — so a radio group was asking the
 * curator to pick a side about an object that does not have one, and then to
 * remember to tick the "and also" flags separately. "Tick what it is" is one
 * question instead of two, and it matches the shape of the review grid, which
 * reviews `applianceKind`, `isSafe` and `hasDisplayCase` as three independent
 * cells with their own confidences.
 *
 * The primary `applianceKind` is DERIVED from these at write time rather than
 * chosen — see {@link resolveCapabilitiesWrite}.
 */
export interface ApplianceCapabilities {
  /** It winds. */
  winder: boolean;
  /** It has the security properties of a safe. */
  safe: boolean;
  /** It is a display case — a glazed surface watches are viewed through. */
  displayCase: boolean;
}

/**
 * The checkbox state an existing entry opens with.
 *
 * ⚠️ SEEDED FROM THE CLASSIFIER, not from the raw fields, and this is what
 * stops the "at least one must be ticked" rule blocking a save that used to
 * work. A legacy entry carries no `applianceKind`, no `isSafe` and no
 * `hasDisplayCase` at all — three empty boxes and a validation error — but
 * `resolveApplianceKind` correctly reads it as a winder, so it opens with
 * exactly one box ticked and saves unchanged.
 */
export function capabilitiesOf(
  entry: ApplianceKindInput & Pick<WinderEntry, 'hasDisplayCase'>,
): ApplianceCapabilities {
  const kind = resolveApplianceKind(entry);
  return {
    winder: kind === 'winder',
    safe: kind === 'safe' || entry.isSafe === true,
    displayCase:
      kind === 'display_case' || entry.hasDisplayCase === true,
  };
}

/** Whether the capability set is legal to save. At least one must be ticked. */
export function capabilitiesAreValid(caps: ApplianceCapabilities): boolean {
  return caps.winder || caps.safe || caps.displayCase;
}

/**
 * What a save should STORE, given the three capability checkboxes.
 *
 * Pure, and separate from `saveWinder`, so the rules below are testable without
 * a Firestore double — they are invariants about the document, and `saveWinder`
 * itself is a network call wrapped around a payload literal.
 *
 * ## The ladder
 *
 * ```
 * winds?        -> 'winder'        (even if it also protects and displays)
 * else safe?    -> 'safe'          (even if it has a viewing window)
 * else display? -> 'display_case'
 * ```
 *
 * Exactly one rung fires. The tie-break is whether it WINDS, not whether it
 * protects: a safe misfiled as a winder merely shows an empty winding section,
 * while a winder misfiled as a safe has its turns-per-day suppressed
 * everywhere.
 *
 * ## ⚠️ 1. `applianceKind` is NEVER the literal `'winder'`
 *
 * The ladder DERIVES winder; the write stores its ABSENCE. shared-types is
 * explicit that absent means winder, that no backfill is needed and that none
 * should be run. The editor always holds a capability set, so writing the
 * derived value straight through would stamp an explicit `'winder'` onto every
 * entry anyone opens and saves — inventing data the document never carried, one
 * edit at a time, across the whole catalogue. `buildWinderIndexRecord` declines
 * to invent the same value for the same reason.
 *
 * A `'safe'` or a `'display_case'` IS written explicitly: that is the new
 * information.
 *
 * ## 2. The flags follow the boxes, not the kind
 *
 * `isSafe` and `hasDisplayCase` are what the ticked boxes mean, and they are
 * INDEPENDENT of the primary kind — `isSafe: true` with `applianceKind` absent
 * is the winder-safe, and is the expected shape at the top of this market.
 * Every existing consumer of the security and display blocks is gated on these
 * flags, so setting them lights all of them up with no change of their own.
 *
 * ## 3. What is not ticked is suppressed on the way out
 *
 * The editor deliberately KEEPS hidden values so a mis-tick is undoable, which
 * means the form will happily hand over a display case's leftover turns-per-day.
 * The write path is what refuses it — the same division of labour
 * `resolveSafeSpecsWrite` and `resolveTurnsPerDayWrite` already use.
 */
export function resolveCapabilitiesWrite(caps: ApplianceCapabilities): {
  applianceKind: 'safe' | 'display_case' | null;
  isSafe: boolean;
  hasDisplayCase: boolean;
  suppressWinding: boolean;
  suppressSafety: boolean;
  suppressDisplay: boolean;
} {
  const applianceKind = caps.winder
    ? null
    : caps.safe
      ? 'safe'
      : caps.displayCase
        ? 'display_case'
        : null;

  return {
    applianceKind,
    isSafe: caps.safe,
    hasDisplayCase: caps.displayCase,
    suppressWinding: !caps.winder,
    suppressSafety: !caps.safe,
    suppressDisplay: !caps.displayCase,
  };
}
